import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    IconButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    FormGroup,
    FormControlLabel,
    CircularProgress
} from '@mui/material';
import { Search, Add, Edit, Person, AdminPanelSettings, Delete } from '@mui/icons-material';
import { useAllAdmins, useCreateAdmin, useUpdateAdmin, useDeleteAdmin } from 'hooks/useStaff';
import PermissionGuard from 'ui-component/PermissionGuard';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ADMIN_ROLES = [
    { value: 'superadmin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'finance', label: 'Finance' },
    { value: 'support', label: 'Support' },
    { value: 'customer_care', label: 'Customer Care' },
    { value: 'agent', label: 'Agent' }
];

const ADMIN_PERMISSIONS = [
    { group: 'User Management', value: 'manage_admins', label: 'Platform Admins' },
    { group: 'User Management', value: 'manage_vendors', label: 'Vendors & Stores' },
    { group: 'User Management', value: 'manage_users', label: 'Customers' },
    { group: 'User Management', value: 'manage_riders', label: 'Delivery Riders' },
    { group: 'User Management', value: 'manage_staffs', label: 'Vendor Staff' },
    { group: 'User Management', value: 'manage_collections', label: 'Staff Collections' },
    { group: 'Operations', value: 'manage_orders', label: 'Order Tracking' },
    { group: 'Operations', value: 'manage_categories', label: 'Categories & Segments' },
    { group: 'Financials', value: 'manage_finance', label: 'Revenue & Payouts' },
    { group: 'Marketing', value: 'manage_promotions', label: 'Promotions & Coupons' },
    { group: 'Marketing', value: 'manage_banners', label: 'Marketing Banners' },
    { group: 'Support', value: 'manage_support', label: 'Help Desk / Tickets' },
    { group: 'Compliance', value: 'manage_kyc', label: 'KYC Verification' },
    { group: 'System', value: 'manage_logs', label: 'Audit Logs' },
    { group: 'System', value: 'manage_configurations', label: 'System Config' }
];

export default function PlatformEmployeeList() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);

    const {
        data: adminData,
        isLoading,
        refetch
    } = useAllAdmins({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined
    });

    const createMutation = useCreateAdmin();
    const updateMutation = useUpdateAdmin();
    const deleteMutation = useDeleteAdmin();

    const admins = adminData?.data || [];
    const totalAdmins = adminData?.total || 0;

    const handleOpenModal = (employee = null) => {
        setEditingEmployee(employee);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingEmployee(null);
        setModalOpen(false);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
            try {
                await deleteMutation.mutateAsync(id);
                toast.success('Employee deleted successfully');
            } catch (err) {
                console.error(err);
            }
        }
    };

    return (
        <Box p={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={700}>
                    Platform Employees
                </Typography>
                <PermissionGuard permission="manage_admins" action="create">
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
                        Add Internal Staff
                    </Button>
                </PermissionGuard>
            </Stack>

            <Paper sx={{ width: '100%', borderRadius: 3, p: 2 }}>
                <TextField
                    placeholder="Search by name or email..."
                    size="small"
                    sx={{ width: 350, mb: 3 }}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name / Role</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Permissions</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading
                                ? [...Array(5)].map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={5}>
                                              <Skeleton height={50} />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : admins.map((admin) => (
                                      <TableRow key={admin.id} hover>
                                          <TableCell>
                                              <Stack direction="row" spacing={2} alignItems="center">
                                                  <Avatar sx={{ bgcolor: admin.role === 'superadmin' ? 'error.main' : 'primary.main' }}>
                                                      <AdminPanelSettings fontSize="small" />
                                                  </Avatar>
                                                  <Box>
                                                      <Typography variant="subtitle2" fontWeight={600}>
                                                          {admin.name}
                                                      </Typography>
                                                      <Chip label={admin.role} size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
                                                  </Box>
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Typography variant="body2">{admin.email}</Typography>
                                          </TableCell>
                                          <TableCell>
                                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                  {admin.permissions?.map((p) => (
                                                      <Chip
                                                          key={p}
                                                          label={p.replace('manage_', '')}
                                                          size="small"
                                                          variant="outlined"
                                                          sx={{ fontSize: '0.6rem' }}
                                                      />
                                                  ))}
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Chip
                                                  label={admin.status}
                                                  size="small"
                                                  color={admin.status === 'active' ? 'success' : 'error'}
                                              />
                                          </TableCell>
                                          <TableCell align="right">
                                              <IconButton onClick={() => handleOpenModal(admin)} color="primary">
                                                  <Edit fontSize="small" />
                                              </IconButton>
                                              <IconButton onClick={() => handleDelete(admin.id)} color="error">
                                                  <Delete fontSize="small" />
                                              </IconButton>
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalAdmins}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
            </Paper>

            <AdminStaffModal
                open={modalOpen}
                onClose={handleCloseModal}
                admin={editingEmployee}
                createMutation={createMutation}
                updateMutation={updateMutation}
            />
            <ToastContainer position="top-right" autoHideDuration={5000} />
        </Box>
    );
}

function AdminStaffModal({ open, onClose, admin, createMutation, updateMutation }) {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        role: 'admin',
        permissions: []
    });

    React.useEffect(() => {
        if (admin) {
            const [firstName = '', ...lastNameParts] = (admin.name || '').split(' ');
            setFormData({
                firstName,
                lastName: lastNameParts.join(' '),
                email: admin.email || '',
                phoneNumber: admin.phone || '',
                role: admin.role || 'admin',
                permissions: admin.permissions || []
            });
        } else {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                role: 'admin',
                permissions: []
            });
        }
    }, [admin, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (perm) => {
        setFormData((prev) => {
            const has = prev.permissions.includes(perm);
            return {
                ...prev,
                permissions: has ? prev.permissions.filter((p) => p !== perm) : [...prev.permissions, perm]
            };
        });
    };

    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    const handleSubmit = async () => {
        try {
            if (admin) {
                await updateMutation.mutateAsync({ id: admin.id, payload: formData });
            } else {
                await createMutation.mutateAsync(formData);
            }
            onClose();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{admin ? 'Edit Platform Employee' : 'Add New Platform Employee'}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} pt={1}>
                    {!admin && (
                        <>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select name="role" value={formData.role} label="Role" onChange={handleChange}>
                                {ADMIN_ROLES.map((role) => (
                                    <MenuItem key={role.value} value={role.value}>
                                        {role.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" mb={1}>
                            Access Permissions
                        </Typography>
                        <FormGroup>
                            <Grid container spacing={1}>
                                {ADMIN_PERMISSIONS.map((p) => (
                                    <Grid item xs={6} key={p.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={formData.permissions.includes(p.value)}
                                                    onChange={() => handlePermissionChange(p.value)}
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        display="block"
                                                        sx={{ fontSize: '0.65rem', lineHeight: 1 }}
                                                    >
                                                        {p.group}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                        {p.label}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
                >
                    {isSubmitting ? 'Processing...' : admin ? 'Update' : 'Create Account'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
